import moment from "moment";
import { FormattedMessage } from "react-intl";
import { Column, Row } from "react-table";
import { Constants } from "../../../constants";
import { EcgActionSeverity } from "../../../models/ecg.interface";
import ApprovedIcon from "../../Shared/ApprovedIcon/ApprovedIcon";
import CircleIndicator from "../../Shared/CircleIndicator/CircleIndicator";
import EcgDateTime from "../../Shared/EcgDateTime/EcgDateTime";
import HighlightText from "../../Shared/HighlightText/HighlightText";
import HomeIcon from "../../Shared/HomeIcon/HomeIcon";
import InterpretationIcon from "../../Shared/InterpretationIcon/InterpretationIcon";
import ReviewPriorityIndicator from "../../Shared/ReviewTimeIndicator/ReviewPriorityIndicator";
import TableColumnHeader from "../../Shared/Table/TableColumnHeader/TableColumnHeader";
import UrgentIcon from "../../Shared/UrgentIcon/UrgentIcon";
import { EcgTableItem } from "./EcgsTable";
import styles from "./EcgsTable.module.css";

import { Popover } from "@shltelemedicine/shl-ui-lib";
import { getActionHeader, getActionIcon } from "../../../utils/ecgUtil";

export const getEcgsTableColumns = (opts?: {
  highlightText?: string;
  isReviewer?: boolean;
  isCroOrg?: boolean;
  isApprover?: boolean;
  isCroExternOrg?: boolean;
  isProfessional?: boolean;
}): Column<EcgTableItem>[] => {
  const {
    isReviewer,
    isCroOrg,
    isApprover,
    highlightText,
    isCroExternOrg,
    isProfessional,
  } = opts ?? {};

  const getActionRequiredIcon = (severity: EcgActionSeverity) => (
    <img
      style={{ width: "1.2rem" }}
      alt={getActionHeader(severity)}
      src={getActionIcon(severity)}
    />
  );

  let columns: (Column<EcgTableItem> & { className?: string })[] = [
    {
      accessor: "isUrgent",
      Header: (
        <TableColumnHeader center>
          <UrgentIcon />
        </TableColumnHeader>
      ),
      Cell: ({ row }) => (
        <div className={`${!row.original.unviewedEcg ? "viewed-ecg" : ""}`}>
          {row.original?.isUrgent ? (
            <div className="d-flex justify-content-center">
              <UrgentIcon />
            </div>
          ) : null}
        </div>
      ),
      width: "1%",
    },
    {
      accessor: "date",
      Header: ({ column }) => (
        <TableColumnHeader
          isSorted={column.isSorted}
          isSortedDesc={column.isSortedDesc}
        >
          <span className="capitalized">
            <FormattedMessage
              id="USER.ECG.DATE.TIME"
              defaultMessage="Date & Time"
            />
          </span>
        </TableColumnHeader>
      ),
      Cell: ({ row }) => (
        <span
          className={`${row.original.unviewedEcg ? "font-weight-bold" : ""}`}
        >
          <EcgDateTime
            isEcgArchived={!!row.original.isArchived}
            ecgHasSymptoms={!!row.original.hasSymptoms}
            isEcgUnreadable={!!row.original.isUnreadable}
            date={row.original?.date}
            showTime
          />
        </span>
      ),
    },
    {
      accessor: "reviewPriority",
      Header: (
        <TableColumnHeader center>
          <span className="capitalized">
            <FormattedMessage id="USER.ECG.SLA" defaultMessage="SLA" />
          </span>
        </TableColumnHeader>
      ),
      Cell: ({ row }: { row: Row<EcgTableItem> }) => {
        return (
          <div className="d-flex justify-content-center">
            {!row.original.isReviewed ? (
              <ReviewPriorityIndicator
                priority={row.original.reviewPriority ?? "none"}
                minutesRemaining={row.original.minutesRemaining}
              />
            ) : null}
          </div>
        );
      },
      width: "5%",
    },
    {
      accessor: "isApproved",
      Header: (
        <TableColumnHeader center>
          <ApprovedIcon />
        </TableColumnHeader>
      ),
      Cell: ({ row }) =>
        (row.original?.isApproved && (
          <div className="d-flex justify-content-center">
            <ApprovedIcon />
          </div>
        )) ??
        null,
      width: "5%",
    },
    {
      accessor: "hasFindings",
      Header: (
        <TableColumnHeader center>
          <CircleIndicator className={styles.findings} />
        </TableColumnHeader>
      ),
      Cell: ({ row }) =>
        row.original?.hasFindings ? (
          <div className="d-flex justify-content-center">
            <CircleIndicator className={styles.findings} />
          </div>
        ) : null,
      width: "5%",
    },
    {
      accessor: "hasInterpretations",
      Header: (
        <TableColumnHeader center>
          <InterpretationIcon selected />
        </TableColumnHeader>
      ),
      Cell: ({ row }) => {
        const hasActionRequired = !!row.original.actionSeverity;
        return (
          row.original?.hasInterpretations &&
          (hasActionRequired ? (
            getActionRequiredIcon(row.original.actionSeverity!)
          ) : (
            <InterpretationIcon selected />
          ))
        );
      },
      width: "3%",
    },
    {
      accessor: "state",
      Header: ({ column }) => (
        <TableColumnHeader
          isSorted={column.isSorted}
          isSortedDesc={column.isSortedDesc}
        >
          <span className="capitalized">
            <FormattedMessage
              id="MANAGEMENT.ADD_ENTITY.ADDRESS.STATE"
              defaultMessage="State"
            />
          </span>
        </TableColumnHeader>
      ),
      Cell: ({ row }: { row: Row<EcgTableItem> }) => (
        <span
          className={`${row.original.unviewedEcg ? "font-weight-bold" : ""}`}
        >
          <HighlightText
            text={row.original?.state ?? ""}
            highlightText={[highlightText ?? ""]}
          />
        </span>
      ),
    },
    {
      accessor: "patientName",
      Header: ({ column }) => (
        <TableColumnHeader
          isSorted={column.isSorted}
          isSortedDesc={column.isSortedDesc}
        >
          <span className="capitalized">
            <FormattedMessage
              id="PATIENT.ECG.PATIENT"
              defaultMessage="Patient"
            />
          </span>
        </TableColumnHeader>
      ),
      Cell: ({ row }: { row: Row<EcgTableItem> }) => (
        <span
          className={`${row.original.unviewedEcg ? "font-weight-bold" : ""}`}
        >
          <HighlightText
            text={row.original?.patientName ?? null}
            highlightText={[highlightText ?? ""]}
          />
        </span>
      ),
    },
    {
      accessor: "dateOfBirth",
      Header: ({ column }) => (
        <TableColumnHeader
          isSorted={column.isSorted}
          isSortedDesc={column.isSortedDesc}
        >
          <span className="capitalized">
            {isCroOrg || isCroExternOrg ? (
              <FormattedMessage id="PATIENT.ECG.YOB" defaultMessage="YOB" />
            ) : (
              <FormattedMessage id="PATIENT.ECG.DOB" defaultMessage="DOB" />
            )}
          </span>
        </TableColumnHeader>
      ),
      Cell: ({ row }) => (
        <span
          className={`${row.original.unviewedEcg ? "font-weight-bold" : ""}`}
        >
          <HighlightText
            text={moment(row.original?.dateOfBirth).format(
              isCroOrg || isCroExternOrg
                ? Constants.YEAR_FORMAT
                : Constants.DATE_FORMAT
            )}
            highlightText={[highlightText ?? ""]}
          />
        </span>
      ),
    },
    {
      accessor: "orgName",
      Header: ({ column }) => (
        <TableColumnHeader
          isSorted={column.isSorted}
          isSortedDesc={column.isSortedDesc}
        >
          <span className="capitalized">
            <FormattedMessage
              id="PATIENT.ORGANIZATION"
              defaultMessage="ORGANIZATION"
            />
          </span>
        </TableColumnHeader>
      ),
      Cell: ({ row }) => (
        <div
          className={`${
            row.original.unviewedEcg ? "font-weight-bold" : ""
          } d-flex align-items-center`}
        >
          <HomeIcon />
          <span className="mx-sm"></span>
          <Popover
            hoverContent={
              <EntityHoverContent
                name={row.original?.entityName}
                email={row.original?.entityEmail}
                phone={row.original?.entityPhone}
              />
            }
            id="entityData"
          >
            <HighlightText
              text={row.original?.orgName ?? ""}
              highlightText={[highlightText ?? ""]}
            />
          </Popover>
        </div>
      ),
    },
    {
      accessor: "entityName",
      Header: ({ column }) => (
        <TableColumnHeader
          isSorted={column.isSorted}
          isSortedDesc={column.isSortedDesc}
        >
          <span className="capitalized">
            <FormattedMessage id="ENTITY" defaultMessage="ENTITY" />
          </span>
        </TableColumnHeader>
      ),
      Cell: ({ row }) => (
        <Popover
          id={"entityName"}
          hoverContent={
            <EntityHoverContent
              name={row.original?.entityName}
              email={row.original?.entityEmail}
              phone={row.original?.entityPhone}
            />
          }
        >
          <div className={row.original.unviewedEcg ? "font-weight-bold" : ""}>
            <HighlightText
              text={row.original?.entityName ?? ""}
              highlightText={[highlightText ?? ""]}
            />
          </div>
        </Popover>
      ),
    },
    {
      accessor: "reference",
      Header: ({ column }) => (
        <TableColumnHeader
          isSorted={column.isSorted}
          isSortedDesc={column.isSortedDesc}
        >
          <span className="capitalized">
            <FormattedMessage id="USER.REFERENCE" defaultMessage="Reference" />
          </span>
        </TableColumnHeader>
      ),
      Cell: ({ row }) => (
        <span
          className={`${row.original.unviewedEcg ? "font-weight-bold" : ""}`}
        >
          <HighlightText
            text={row.original?.reference ?? ""}
            highlightText={[highlightText ?? ""]}
          />
        </span>
      ),
    },
  ];

  if (isReviewer) {
    return columns.filter(
      (column) =>
        column.accessor !== "hasFindings" &&
        column.accessor !== "isApproved" &&
        column.accessor !== "entityName"
    );
  }

  if (!isReviewer) {
    columns = columns.filter((column) => column.accessor !== "state");
  }

  if (isApprover) {
    return columns.filter(
      (column) =>
        column.accessor !== "hasFindings" &&
        column.accessor !== "reviewPriority"
    );
  }

  if (isProfessional) {
    return columns.filter(
      (column) =>
        column.accessor !== "reviewPriority" &&
        column.accessor !== "isApproved" &&
        column.accessor !== "entityName" &&
        column.accessor !== "orgName"
    );
  }

  return columns.filter(
    (column) =>
      column.accessor !== "reviewPriority" && column.accessor !== "isApproved"
  );
};

type EntityHoverContentProps = {
  name?: string;
  email?: string;
  phone?: string;
};

const EntityHoverContent = ({
  name,
  email,
  phone,
}: EntityHoverContentProps) => {
  return (
    <div>
      <table className="w-100 table mb-0">
        <thead>
          <tr>
            <th className="border-0 p-1">
              <h6 className="mb-1">{name}</h6>
            </th>
            <th className="border-0 p-1"></th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="p-1">
              <i className="fas fa-phone font-sm"></i>
              &nbsp; {phone || "N/A"}
            </td>
          </tr>
          <tr>
            <td className="p-1">
              <i className="far fa-envelope"></i>
              &nbsp; {email || "N/A"}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};
